'use client';

import { useEffect, useRef, useState } from 'react';
import { toast } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { acceptStringFor } from '@/lib/constants/file-type-accept';
import {
  AddDocument,
  addDocumentSourceIcon,
  addDocumentSourceLabel,
  useMatterRequirementAttach,
  usePmsProviderLabel
} from '@/components/shared/add-document';
import { DEFAULT_REPLACE_SOURCES } from './types';
import type { ReplaceDocumentProps, ReplaceDocumentSource } from './types';

/**
 * The Replace flow itself, driven by the caller's own `open` state: the source
 * chooser and, once a source is picked, that source's picker or dialog. It
 * renders **no trigger**. {@link useReplaceDocument} is the trigger for a
 * surface with an overflow menu, and a surface whose Replace is dispatched from
 * somewhere else (the Documents tab routes every core slot through one
 * `onReplace(type)` callback) drives this directly.
 *
 * See {@link useReplaceDocument} for what the seam is and why.
 *
 * **Render it outside any dropdown.** A `DropdownMenuContent` unmounts its
 * children when the menu closes, and selecting an item closes the menu, so a
 * dialog or file input rendered inside one is torn down in the same commit that
 * was meant to open it.
 */
export function ReplaceDocument({
  open,
  onOpenChange,
  matterId,
  target,
  replacingFileId,
  sources = DEFAULT_REPLACE_SOURCES,
  accept,
  pmsProviderLabel,
  onSelectUpload,
  onReplaced,
  onReplacingChange,
  label = 'Replace'
}: ReplaceDocumentProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // Which source the user picked, when it is one the shared AddDocument dialogs
  // land. Upload is not among them: it is a one-click OS picker, so it opens no
  // dialog of its own.
  const [dialogSource, setDialogSource] = useState<'existing' | 'pms' | null>(
    null
  );

  // The seam's single file input, addressed by ref and never by a DOM id. That
  // collision made Replace on the second card of a type open the first card's
  // picker (VALE-895).
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload: uploadAction } = useMatterRequirementAttach(matterId, target);

  // What the picker offers comes from the target's own file type, via the one
  // registry that states it (`acceptStringFor`, VALE-209). Every hand-rolled
  // Replace input this seam replaces carried its own literal, and they had
  // already drifted apart. A caller overrides only where the requirement is
  // genuinely narrower than its file type.
  const pickerAccept =
    accept ?? (target ? acceptStringFor(target.fileType) : undefined);

  // The PMS gate is `usePmsProviderLabel` and nothing else (LF-6261, VALE-356):
  // one definition of "is this matter PMS-connected, and may we offer its
  // documents". A surface listing `'pms'` on a matter that is not connected, or
  // is synced from a provider whose inbound import does not work, simply does
  // not see the source.
  const resolvedPmsLabel = usePmsProviderLabel(
    matterId,
    sources.includes('pms') && !pmsProviderLabel
  );
  const providerLabel = pmsProviderLabel ?? resolvedPmsLabel;

  // No target means nowhere for a replacement to land, so the seam offers
  // nothing rather than a flow that opens onto a dead end.
  const visibleSources = target
    ? sources.filter((source) => source !== 'pms' || !!providerLabel)
    : [];
  const soleSource =
    visibleSources.length === 1 ? visibleSources[0] : undefined;

  const replaceFromFile = async (file: File) => {
    if (!uploadAction) return;
    onReplacingChange?.(true);
    try {
      const ids = await uploadAction([file], {});
      if (ids.length > 0) await onReplaced?.();
    } catch {
      toast.error('Failed to upload document');
    } finally {
      onReplacingChange?.(false);
    }
  };

  const runSource = (source: ReplaceDocumentSource) => {
    if (source === 'upload') {
      // The flow ends here: the OS picker (or the caller's own upload UI) is not
      // something this component keeps open state for.
      onOpenChange(false);
      if (onSelectUpload) onSelectUpload();
      else fileInputRef.current?.click();
      return;
    }
    setDialogSource(source);
  };

  // Upload is the one source that cannot be opened declaratively (a file picker
  // is a `.click()`, not a rendered element), so a flow that opens straight onto
  // it needs this. Every other single-source case renders its dialog below with
  // no effect at all, and the multi-source case waits for the chooser.
  const openedRef = useRef(false);
  const autoRunUpload = open && soleSource === 'upload';
  useEffect(() => {
    if (!autoRunUpload) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    onOpenChange(false);
    if (onSelectUpload) onSelectUpload();
    else fileInputRef.current?.click();
  }, [autoRunUpload, onSelectUpload, onOpenChange]);

  const showChooser = open && !dialogSource && visibleSources.length > 1;
  // A single non-upload source opens its own dialog directly, with no chooser of
  // one in between.
  const activeDialogSource =
    dialogSource ??
    (open && soleSource && soleSource !== 'upload' ? soleSource : null);

  const closeAll = () => {
    setDialogSource(null);
    onOpenChange(false);
  };

  return (
    <>
      {/* The seam's only file input. Reset on change so the SAME file can be
          re-picked after a failed replace. Otherwise no change event fires and
          the retry is silently impossible. */}
      {!onSelectUpload && visibleSources.includes('upload') && (
        <input
          ref={fileInputRef}
          type="file"
          accept={pickerAccept}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) void replaceFromFile(file);
          }}
        />
      )}

      {/* More than one source: name them before anything opens. The labels come
          from `AddDocument` rather than from a second set of literals here: the
          dialog the user lands in is that component's, so its wording is the one
          that has to match. */}
      {showChooser && (
        <Dialog open onOpenChange={(next) => !next && closeAll()}>
          <DialogContent className="max-w-xs gap-3 p-4">
            <DialogHeader className="space-y-0">
              <DialogTitle className="text-sm font-semibold">
                {label}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Choose where the replacement comes from.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-1.5">
              {visibleSources.map((source) => (
                <Button
                  key={source}
                  type="button"
                  variant="outline"
                  size="sm"
                  density="micro"
                  align="start"
                  onClick={() => runSource(source)}
                >
                  {addDocumentSourceIcon(source, 'mr-2 h-3.5 w-3.5')}
                  {addDocumentSourceLabel(source, providerLabel)}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Choose-existing / PMS land through the shared component, controlled and
          single-source so each opens its own dialog directly. Same `target` as
          the upload above, so a replacement is the same file whichever source
          produced it. */}
      {activeDialogSource && target && (
        <AddDocument
          matterId={matterId}
          sources={[activeDialogSource]}
          target={target}
          pmsProviderLabel={providerLabel}
          excludeFileIds={
            replacingFileId != null ? [replacingFileId] : undefined
          }
          open
          onOpenChange={(next) => {
            if (!next) closeAll();
          }}
          onAttached={onReplaced}
        />
      )}
    </>
  );
}
